<template>
  <div class="laboratorios-page">
    <!-- Hero Section -->
    <section
      class="hero-section hero-animate"
      :style="{ backgroundImage: `url(${heroBackgroundImage})` }">
      <div class="container">
        <div class="row">
          <div class="col-lg-10 mx-auto">
            <div class="hero-content">
              <h1 class="hero-title-main">Cuidamos tu salud</h1>
              <h2 class="hero-title-sub">con precisión y confianza</h2>
              <p class="hero-description">
                Más de 3,000 exámenes de laboratorio disponibles con descuentos de hasta 30%. Detecta problemas a tiempo, ahorra cientos de dólares al año y mantén tu salud bajo control. Resultados rápidos, laboratorios certificados y atención que entiende tu idioma.
              </p>
              <div class="hero-buttons">
                <router-link to="/contact" class="btn btn-primary-hero btn-lg">
                  Contáctanos
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Important Section -->
    <section class="why-important-section">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-6">
            <div class="content-wrapper animate-on-scroll fade-in-left">
              <h2 class="section-title">
                ¿Por qué son importantes las pruebas de laboratorio?
              </h2>
              <p class="section-text">
                <strong>Imagina detectar la diabetes antes de que cause daños irreversibles.</strong> O descubrir un problema cardíaco cuando aún es tratable. <strong>Los análisis de laboratorio</strong> no son solo números: son tu primera línea de defensa. <strong>Un análisis de sangre</strong> anual puede revelar lo que tu cuerpo está tratando de decirte, antes de que sea demasiado tarde.
              </p>
              <p class="section-text">
                <strong>La prevención cuesta menos que el tratamiento.</strong> Un análisis que cuesta $50 puede ahorrarte miles en tratamientos futuros. Por eso los mejores médicos del mundo confían en estos estudios.
              </p>
              <p class="section-text">
                <strong>Con Hispano Medical Plan, no tienes que elegir entre tu salud y tu economía.</strong> Con descuentos de hasta 30% y el beneficio Wellness Visit incluido, puedes hacerte los análisis que necesitas sin romper tu presupuesto. <strong>Tu salud es una inversión, no un gasto.</strong>
              </p>
              <router-link to="/plans" class="btn btn-why-important mt-4">
                Activa tu beneficio
                <i class="fas fa-arrow-right ml-2"></i>
              </router-link>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="image-wrapper animate-on-scroll fade-in-right">
              <img :src="bannerImage" alt="Laboratorio" class="section-image" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Prevention Section -->
    <section class="prevention-section">
      <div class="container">
        <div class="section-header text-center mb-5 animate-on-scroll fade-in-up">
          <h2 class="section-title">Prevención Integral</h2>
          <h3 class="section-subtitle">Wellness Visit</h3>
          <p class="section-description">
            El chequeo completo que puede salvar tu vida. Detecta problemas antes de que se conviertan en emergencias. Un valor de más de $500 incluido en tu plan, sin costo adicional.
          </p>
          <p class="section-description">
            Disponible para
            <strong>{{ planNamesText }}</strong> Tu salud preventiva, al alcance de todos.
          </p>
        </div>

        <div class="row">
          <div class="col-lg-4 col-md-6 mb-4">
            <div class="benefit-card animate-on-scroll fade-in-up">
              <div class="benefit-icon">
                <img :src="labIcon" alt="Laboratorio" class="benefit-icon-image" />
              </div>
              <h4 class="benefit-title">Laboratorio Extendido</h4>
              <p class="benefit-text">
                Un chequeo completo que normalmente costaría $500+, incluido en tu plan. Análisis de sangre con 32 parámetros que revelan todo sobre tu salud, Hemoglobina A1c para detectar diabetes, y análisis de orina completo. Todo lo que necesitas para conocer tu estado de salud real.
              </p>
            </div>
          </div>

          <div class="col-lg-4 col-md-6 mb-4">
            <div
              class="benefit-card benefit-card-center animate-on-scroll fade-in-up"
              style="animation-delay: 0.1s">
              <div class="benefit-icon">
                <img :src="heartIcon" alt="Salud Cardíaca" class="benefit-icon-image" />
              </div>
              <h4 class="benefit-title">Salud Cardíaca</h4>
              <p class="benefit-text">
                Tu corazón es tu motor. El Electrocardiograma (EKG) incluido detecta problemas cardíacos antes de que se conviertan en emergencias. Una evaluación que puede salvar tu vida, sin costo adicional. La prevención cardíaca nunca fue tan accesible.
              </p>
            </div>
          </div>

          <div class="col-lg-4 col-md-6 mb-4">
            <div
              class="benefit-card animate-on-scroll fade-in-up"
              style="animation-delay: 0.2s">
              <div class="benefit-icon">
                <img
                  :src="diagnosticIcon"
                  alt="Imágenes Diagnósticas"
                  class="benefit-icon-image" />
              </div>
              <h4 class="benefit-title">Imágenes Diagnósticas</h4>
              <p class="benefit-text">
                Ultrasonidos que normalmente costarían $300-500, incluidos en tu Wellness Visit. Detección temprana de problemas en mama o abdomen que pueden salvar vidas. Sin costos adicionales, sin esperas largas. Tu salud preventiva, al alcance de todos.
              </p>
            </div>
          </div>
        </div>

        <div class="coverage-section mt-5">
          <div class="container">
            <div class="row align-items-center">
              <div class="col-lg-6 col-12">
                <div class="coverage-card animate-on-scroll fade-in-left">
          <h4 class="coverage-title">Cobertura 100% Garantizada</h4>
          <div class="coverage-details">
            <p>
              • <strong>1 Visita/Año:</strong> {{ planNamesOneVisit }} (por adulto). Un chequeo completo anual, sin pagar un centavo extra.
            </p>
            <p>• <strong>2 Visitas/Año:</strong> {{ planNamesTwoVisits }}. Doble protección, doble tranquilidad. Tu salud, dos veces al año, completamente cubierta.</p>
          </div>
                  <router-link to="/plans" class="btn btn-coverage mt-4">
                    Activa tu beneficio
                    <i class="fas fa-arrow-right ml-2"></i>
                  </router-link>
                </div>
              </div>
              <div class="col-lg-6 d-none d-lg-block">
                <div class="coverage-image-wrapper animate-on-scroll fade-in-right">
                  <img :src="coverageImage" alt="Cobertura" class="coverage-image" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Providers Section -->
    <section class="providers-section">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-6">
            <div class="content-wrapper animate-on-scroll fade-in-left">
              <h2 class="section-title">Proveedores de laboratorio contratados</h2>
              <p class="section-text">
                <strong>No trabajamos con cualquiera.</strong> <strong>Hispano Medical Plan</strong> se asocia únicamente con 
                <strong>laboratorios certificados y acreditados a nivel nacional</strong> - los mismos que usan los mejores hospitales del país. 
                <strong>Equipos de última generación, resultados confiables, ubicaciones cerca de ti.</strong>
              </p>
              <p class="section-text">
                <strong>¿No puedes salir de casa?</strong> Muchos de nuestros laboratorios ofrecen <strong>toma de muestras en tu domicilio</strong> por una tarifa mínima. 
                <strong>Tu salud no debería esperar por tu agenda.</strong> Con nuestra red, tienes acceso a más de 3,000 exámenes, 
                <strong>descuentos reales y la comodidad que necesitas.</strong>
              </p>
              <router-link to="/providers#form-section" class="btn btn-providers mt-4">
                Buscar Proveedores
                <i class="fas fa-arrow-right ml-2"></i>
              </router-link>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="image-wrapper animate-on-scroll fade-in-right">
              <img :src="docCorImage" alt="Proveedores" class="doc-cor-image-clean" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Results Section -->
    <section class="results-section">
      <div class="container">
        <div class="section-header text-center mb-5 animate-on-scroll fade-in-up">
          <h2 class="section-title">Resultados Comunes de Química Sanguínea</h2>
          <p class="section-description">
            Conocer tus números es conocer tu salud. Estos análisis revelan lo que tu cuerpo necesita y te dan el poder de tomar decisiones informadas sobre tu bienestar.
          </p>
        </div>

        <div class="row">
          <div class="col-lg-4 col-md-6 mb-4">
            <div class="result-card result-card-side animate-on-scroll fade-in-up">
              <div class="result-icon">
                <img :src="resultIcon1" alt="Colesterol y Lípidos" class="result-icon-image" />
              </div>
              <h4 class="result-title">Colesterol y Lípidos</h4>
              <p class="result-text">
                El colesterol alto es silencioso pero peligroso. Detectarlo a tiempo puede prevenir infartos y problemas cardíacos graves. Con nuestros análisis, sabrás exactamente dónde estás y qué hacer para proteger tu corazón.
              </p>
            </div>
          </div>

          <div class="col-lg-4 col-md-6 mb-4">
            <div
              class="result-card result-card-center animate-on-scroll fade-in-up"
              style="animation-delay: 0.1s">
              <div class="result-icon">
                <img :src="resultIcon2" alt="Hemoglobina A1c" class="result-icon-image" />
              </div>
              <h4 class="result-title">Hemoglobina A1c</h4>
              <p class="result-text">
                La diabetes no avisa, pero la Hemoglobina A1c sí. Este análisis revela si estás en riesgo o si ya tienes diabetes. Detectar a tiempo puede cambiar completamente tu futuro. Incluido en tu Wellness Visit, sin costo extra.
              </p>
            </div>
          </div>

          <div class="col-lg-4 col-md-6 mb-4">
            <div
              class="result-card result-card-side animate-on-scroll fade-in-up"
              style="animation-delay: 0.2s">
              <div class="result-icon">
                <img :src="resultIcon3" alt="Tiroides" class="result-icon-image" />
              </div>
              <h4 class="result-title">Tiroides</h4>
              <p class="result-text">
                La tiroides controla tu energía, peso y estado de ánimo. Cuando está desbalanceada, afecta toda tu vida. Nuestros análisis detectan problemas tiroideos que muchos médicos pasan por alto. Tu bienestar completo, en un solo examen.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="faq-section">
      <div class="container">
        <div class="section-header text-center mb-5 animate-on-scroll fade-in-up">
          <h2 class="section-title">Preguntas Frecuentes</h2>
        </div>

        <div class="row justify-content-center">
          <div class="col-lg-10">
            <div class="faq-accordion">
              <div
                v-for="(faq, index) in faqsDisplay"
                :key="index"
                class="faq-item animate-on-scroll fade-in-up">
                <div
                  class="faq-question"
                  @click="toggleFaq(index)"
                  :class="{ active: faq.isOpen }">
                  <span class="faq-question-text">{{ faq.question }}</span>
                  <i
                    class="fas fa-chevron-up faq-arrow"
                    :class="{ rotated: faq.isOpen }"></i>
                </div>
                <transition name="slide-fade">
                  <div v-show="faq.isOpen" class="faq-answer">
                    <p>{{ faq.answer }}</p>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import plansService from '@/core/services/api/landings/plans'

export default {
  name: 'Laboratorios',
  data() {
    return {
      plans: [],
      loading: false,
      heroBackgroundImage: require('@/assets/lp/7.png'),
      bannerImage: require('@/assets/lp/8.png'),
      docCorImage: require('@/assets/lp/10.png'),
      labIcon: require('@/assets/lp/Recurso 39@300x l.png'),
      heartIcon: require('@/assets/lp/Recurso 40@300x l.png'),
      diagnosticIcon: require('@/assets/lp/Recurso 41@300x l.png'),
      coverageImage: require('@/assets/lp/9.png'),
      resultIcon1: require('@/assets/lp/Recurso 34@300x.webp'),
      resultIcon2: require('@/assets/lp/Recurso 35@300x.webp'),
      resultIcon3: require('@/assets/lp/Recurso 36@300x.webp'),
      faqs: [
        {
          question: '¿Qué descuentos ofrece Hispano Medical Plan en servicios de laboratorio?',
          answer:
            'Hispano Medical Plan ofrece descuentos significativos en servicios de laboratorio clínico para todos nuestros miembros. ' +
            'Los descuentos varían según el plan que tengas: desde 20% en planes básicos hasta 30% en planes más completos como Tu Salud Plus y Tu Salud Familiar. ' +
            'Estos descuentos aplican en más de 3,000 exámenes de laboratorio disponibles en nuestra red de proveedores acreditados en Dallas y el área metropolitana de Texas. ' +
            'Además, con el beneficio de Wellness Visit incluido en varios planes, puedes acceder a análisis de laboratorio extendidos sin costo adicional.',
          isOpen: false,
        },
        {
          question: '¿Necesito autorización previa para realizar análisis de laboratorio?',
          answer:
            'En la mayoría de los casos, no necesitas autorización previa para realizar análisis de laboratorio de rutina cuando acudes a un proveedor dentro de nuestra red. ' +
            'Sin embargo, para análisis especializados o procedimientos más complejos, puede ser necesario obtener una orden médica de tu proveedor de atención primaria. ' +
            'Te recomendamos contactar con nuestro equipo de atención al miembro o consultar con tu médico antes de realizar cualquier análisis para asegurarte de que esté cubierto por tu plan.',
          isOpen: false,
        },
        {
          question: '¿Cuánto tiempo tardan los resultados de los análisis de laboratorio?',
          answer:
            'El tiempo de entrega de resultados varía según el tipo de análisis. La mayoría de los análisis de rutina, como hemograma completo, química sanguínea básica y análisis de orina, ' +
            'están disponibles entre 24 y 48 horas. Algunos resultados más complejos pueden tardar de 3 a 7 días hábiles. ' +
            'Los resultados de análisis especializados pueden tardar hasta 2 semanas. Tu laboratorio te notificará cuando los resultados estén listos, ' +
            'y muchos de nuestros proveedores ofrecen acceso en línea a tus resultados para mayor comodidad.',
          isOpen: false,
        },
        {
          question: '¿Puedo elegir cualquier laboratorio o debo usar la red de Hispano Medical Plan?',
          answer:
            'Para obtener los mejores descuentos y beneficios, recomendamos utilizar los laboratorios dentro de nuestra red de proveedores acreditados. ' +
            'Hispano Medical Plan trabaja exclusivamente con laboratorios clínicos certificados y acreditados a nivel nacional que cumplen los más altos estándares de calidad. ' +
            'Nuestra red incluye ubicaciones en Dallas y el área metropolitana de Texas, por lo que siempre encontrarás un laboratorio cerca de ti. ' +
            'Si necesitas usar un laboratorio fuera de la red, los descuentos pueden variar, por lo que te recomendamos contactarnos antes para conocer los beneficios disponibles.',
          isOpen: false,
        },
        {
          question: '¿Qué análisis están incluidos en el beneficio Wellness Visit de Hispano Medical Plan?',
          answer: null,
          isOpen: false,
        },
      ],
    }
  },
  computed: {
    planNamesText() {
      if (!this.plans || this.plans.length === 0) return 'nuestros planes.'
      const names = this.plans.map(p => p.name).filter(Boolean)
      if (names.length === 0) return 'nuestros planes.'
      if (names.length === 1) return `Plan ${names[0]}.`
      const last = names[names.length - 1]
      const rest = names.slice(0, -1)
      return `Planes ${rest.join(', ')} y ${last}.`
    },
    planNamesOneVisit() {
      if (!this.plans || this.plans.length === 0) return 'Nuestros planes'
      const half = Math.ceil(this.plans.length / 2)
      const names = this.plans.slice(0, half).map(p => p.name).filter(Boolean)
      if (names.length === 0) return 'Nuestros planes'
      if (names.length === 1) return `Plan ${names[0]}`
      const last = names[names.length - 1]
      const rest = names.slice(0, -1)
      return `Planes ${rest.join(', ')} y ${last}`
    },
    planNamesTwoVisits() {
      if (!this.plans || this.plans.length === 0) return 'Nuestros planes'
      const half = Math.ceil(this.plans.length / 2)
      const names = this.plans.slice(half).map(p => p.name).filter(Boolean)
      if (names.length === 0) return this.planNamesOneVisit
      if (names.length === 1) return `Plan ${names[0]}`
      const last = names[names.length - 1]
      const rest = names.slice(0, -1)
      return `Planes ${rest.join(', ')} y ${last}`
    },
    faqsDisplay() {
      const suffix =
        'Esto incluye análisis de sangre con 32 parámetros, Hemoglobina A1c (HbA1c) para monitoreo de diabetes, y análisis de orina completo (Urinalysis). ' +
        'Además, dependiendo de tu plan, puedes acceder a 1 o 2 visitas de bienestar por año. ' +
        'Este beneficio está diseñado para ayudarte a mantener un control preventivo de tu salud y detectar cualquier problema de manera temprana.'
      return this.faqs.map(faq => ({
        ...faq,
        answer:
          faq.answer ||
          `El beneficio Wellness Visit, disponible en ${this.planNamesText} incluye un paquete completo de análisis de laboratorio. ${suffix}`,
      }))
    }
  },
  mounted() {
    this.loadPlans()
    this.setupScrollAnimations()
  },
  methods: {
    async loadPlans() {
      this.loading = true
      try {
        const response = await plansService.getAllPlans()
        let plansData = []
        if (response && response.status === 'ok' && response.data && Array.isArray(response.data)) {
          plansData = response.data
        } else if (Array.isArray(response)) {
          plansData = response
        } else if (response && response.data && Array.isArray(response.data)) {
          plansData = response.data
        }
        this.plans = plansData
      } catch (error) {
        console.error('Error loading plans:', error)
        this.plans = []
      } finally {
        this.loading = false
      }
    },
    setupScrollAnimations() {
      const observerOptions = {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      }

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible')
          }
        })
      }, observerOptions)

      this.$nextTick(() => {
        const elements = this.$el.querySelectorAll('.animate-on-scroll')
        elements.forEach(el => observer.observe(el))
      })
    },
    toggleFaq(index) {
      this.faqs[index].isOpen = !this.faqs[index].isOpen
    },
  },
}
</script>

<style lang="scss" scoped>
// Importar variables de color centralizadas
@import '../styles/variables';

.laboratorios-page {
  .hero-section {
    position: relative;
    background-size: cover;
    background-position: center right;
    background-repeat: no-repeat;
    padding: 120px 0;
    color: white;
    min-height: 600px;
    display: flex;
    align-items: center;

    .hero-content {
      text-align: center;
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .hero-title-main {
      font-size: 5.5rem;
      font-weight: 700;
      margin-bottom: 0.5rem;
      color: white;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      line-height: 1.2;
      width: fit-content;
    }

    .hero-title-sub {
      font-size: 5rem;
      font-weight: 600;
      margin-bottom: 2.5rem;
      color: white;
      text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
      line-height: 1.2;
      width: fit-content;
    }

    .hero-description {
      font-size: 1.9rem;
      color: white;
      font-weight: 500;
      margin-bottom: 2.5rem;
      text-align: justify;
      text-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
      line-height: 1.6;
      max-width: 100%;
      width: fit-content;
    }

    .btn-primary-hero {
      background: $landing-secondary-color;
      color: white;
      border: none;
      border-radius: 8px;
      padding: 15px 40px;
      font-size: 1.25rem;
      font-weight: 700;
      transition: all 0.3s ease;
      text-transform: uppercase;
      box-shadow: 0 6px 20px rgba(34, 207, 224, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2);

      &:hover {
        background: darken($landing-secondary-color, 10%);
        transform: translateY(-3px);
        box-shadow: 0 10px 30px rgba(34, 207, 224, 0.5), 0 4px 12px rgba(0, 0, 0, 0.3);
        color: white;
        text-decoration: none;
      }
    }
  }

  .why-important-section {
    padding: 100px 0;
    background: white;

    .section-title {
      font-size: 3.5rem;
      font-weight: 700;
      color: $landing-primary-color;
      margin-bottom: 1.5rem;
      line-height: 1.2;
    }

    .section-text {
      font-size: 1.3rem;
      color: $landing-primary-color;
      line-height: 1.8;
      margin-bottom: 1.5rem;

      strong {
        font-weight: 700;
      }
    }

    .section-image {
      width: 100%;
      border-radius: 15px;
      box-shadow: 0 10px 30px rgba(45, 106, 110, 0.1);
    }

    .btn-why-important {
      background: white;
      border: 2px solid #e5eaee;
      color: $landing-primary-color;
      padding: 15px 40px;
      font-weight: 700;
      font-size: 1.35rem;
      border-radius: 8px;
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.08);
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;

      i {
        color: $landing-primary-color;
        font-size: 1.2rem;
        transition: transform 0.3s ease;
      }

      &:hover {
        background: white;
        border-color: $landing-primary-color;
        color: $landing-primary-color;
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15), 0 3px 10px rgba(0, 0, 0, 0.1);
        text-decoration: none;

        i {
          transform: translateX(5px);
        }
      }
    }
  }

  .providers-section {
    padding: 100px 0;
    background: white;

    .section-title {
      font-size: 3.5rem;
      font-weight: 700;
      color: $landing-primary-color;
      margin-bottom: 1.5rem;
      line-height: 1.2;
    }

    .section-text {
      font-size: 1.3rem;
      color: $landing-primary-color;
      line-height: 1.8;
      margin-bottom: 1.5rem;

      strong {
        font-weight: 700;
        color: $landing-primary-color;
      }
    }

    .section-image {
      width: 100%;
      border-radius: 15px;
      box-shadow: 0 10px 30px rgba(45, 106, 110, 0.1);
    }

    .doc-cor-image-clean {
      width: 100%;
      height: auto;
      object-fit: contain;
    }

    .btn-providers {
      background: $landing-primary-color;
      border-color: $landing-primary-color;
      color: white;
      padding: 15px 40px;
      font-weight: 700;
      font-size: 1.25rem;
      border-radius: 8px;
      transition: all 0.3s ease;
      display: inline-flex;
      align-items: center;

      i {
        color: white;
        font-size: 1.2rem;
        transition: transform 0.3s ease;
      }

      &:hover {
        background: $landing-primary-dark;
        border-color: $landing-primary-dark;
        transform: translateY(-2px);
        box-shadow: 0 8px 20px $landing-primary-rgba-40;
        text-decoration: none;
        color: white;

        i {
          transform: translateX(5px);
        }
      }
    }
  }

  .prevention-section {
    padding: 100px 0;
    background: #f8f9fa;

    .section-title {
      font-size: 4.5rem;
      font-weight: 700;
      color: $landing-primary-color;
      margin-bottom: 1.5rem;
      line-height: 1.2;
    }

    .section-subtitle {
      font-size: 2.2rem;
      font-weight: 600;
      color: $landing-primary-color;
      margin-bottom: 1.5rem;
    }

    .section-description {
      font-size: 1.4rem;
      color: $landing-primary-color;
      margin-bottom: 0;
      line-height: 1.7;
      font-weight: 500;

      &:last-of-type {
        margin-bottom: 1.5rem;
      }
    }

    .benefit-card {
      background: white;
      padding: 2.5rem;
      border-radius: 15px;
      text-align: center;
      box-shadow: 0 5px 15px rgba(45, 106, 110, 0.08);
      transition: all 0.3s ease;
      height: 100%;
      border: 2px solid transparent;

      &:hover {
        transform: translateY(-5px);
        box-shadow: 0 10px 25px rgba(45, 106, 110, 0.12);
        border-color: $landing-primary-color;
      }

      &.benefit-card-center {
        background: #f0f0f0;

        &:hover {
          border-color: $landing-primary-color;
        }
      }

      .benefit-icon {
        width: 120px;
        height: 120px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1.5rem;

        .benefit-icon-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }

      .benefit-title {
        font-size: 1.5rem;
        font-weight: 700;
        color: $landing-primary-color;
        margin-bottom: 1rem;
      }

      .benefit-text {
        color: $landing-primary-color;
        line-height: 1.7;
      }
    }

    .coverage-section {
      margin-top: 4rem;
      padding: 0;
      background: transparent;

      .container {
        border: 2px solid $landing-border-light;
        border-radius: 15px;
        padding: 2rem;
        overflow: visible;
        background: white;
        position: relative;
      }

      .row {
        margin: 0;
        align-items: flex-end;
      }

        .coverage-card {
          background: linear-gradient(
            135deg,
            $landing-primary-color 0%,
            $landing-primary-color 80%,
            $landing-secondary-color 100%
          );
          padding: 2.5rem 2.5rem;
          border-radius: 15px;
          box-shadow: 0 10px 30px rgba(13, 43, 69, 0.2);
          position: relative;
          z-index: 2;
          margin: 1rem;
          
          @media (max-width: 1023px) {
            margin: 0;
            width: 100%;
            text-align: center;

            .coverage-title {
              text-align: center;
              font-size: 1.55rem !important;
              word-wrap: break-word;
              overflow-wrap: break-word;
            }

            .coverage-details {
              text-align: center;
            }
          }

        .coverage-title {
          font-size: 4.5rem;
          font-weight: 700;
          color: white;
          margin-bottom: 1.5rem;
          text-align: left;
          line-height: 1.2;
        }

        .coverage-details {
          text-align: left;

          p {
            font-size: 1.3rem;
            color: white;
            margin-bottom: 0.8rem;
            line-height: 1.6;
          }
        }

        .btn-coverage {
          background: $landing-secondary-color;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 15px 40px;
          font-weight: 700;
          font-size: 1.25rem;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          text-transform: uppercase;
          box-shadow: 0 4px 15px rgba(34, 207, 224, 0.3);

          i {
            color: white;
            font-size: 1.2rem;
            transition: transform 0.3s ease;
          }

          &:hover {
            background: darken($landing-secondary-color, 10%);
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(34, 207, 224, 0.4);
            text-decoration: none;
            color: white;

            i {
              transform: translateX(5px);
            }
          }
        }
      }

      .coverage-image-wrapper {
        padding: 0;
        margin: 0;
        position: absolute;
        z-index: 1;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;

        .coverage-image {
          width: 150%;
          height: auto;
          max-width: none;
          border-radius: 0;
          object-fit: cover;
          display: block;
          margin: 0;
          padding: 0;
          position: absolute;
          bottom: 0;
          right: 0;
          top: -218px;
          left: -233px;
          
          @media (min-width: 1400px) {
            top: -218px;
            left: -233px;
            width: 150%;
          }
          
          @media (min-width: 1200px) and (max-width: 1399px) {
            top: -180px;
            left: -200px;
            width: 145%;
          }
          
          @media (min-width: 992px) and (max-width: 1199px) {
            top: -150px;
            left: -170px;
            width: 140%;
          }
          
          @media (min-width: 768px) and (max-width: 991px) {
            top: -120px;
            left: -140px;
            width: 130%;
          }
          
          @media (max-width: 767px) {
            width: 120%;
            position: relative;
            top: 0;
            left: 0;
            bottom: auto;
            right: auto;
          }
        }
      }
    }
  }

  .results-section {
    padding: 100px 0;
    background: linear-gradient(
      135deg,
      $landing-primary-color 0%,
      $landing-primary-color 75%,
      $landing-secondary-color 100%
    );

    .section-title {
      font-size: 3.5rem;
      font-weight: 700;
      color: white;
      margin-bottom: 1.5rem;
      line-height: 1.2;
    }

    .section-description {
      font-size: 1.6rem;
      color: white;
      font-weight: 500;
      margin-bottom: 2rem;
    }

    .result-card {
      padding: 2rem;
      border-radius: 15px;
      text-align: center;
      transition: all 0.3s ease;
      height: 100%;
      border: 2px solid transparent;

      &.result-card-side {
        background: white;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);

        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          border-color: $landing-primary-color;
        }

        .result-title {
          color: $landing-primary-color;
        }

        .result-text {
          color: $landing-primary-color;
        }
      }

      &.result-card-center {
        background: $landing-primary-color;
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
        border: 2px solid transparent;

        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
          border-color: $landing-primary-color;
        }

        .result-title {
          color: white;
        }

        .result-text {
          color: white;
          font-size: 1.2rem;
          line-height: 1.8;
        }
      }

      .result-icon {
        width: 120px;
        height: 120px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1.5rem;

        .result-icon-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
      }

      .result-title {
        font-size: 1.5rem;
        font-weight: 700;
        margin-bottom: 1rem;
      }

      .result-text {
        line-height: 1.7;
        font-size: 1.15rem;
      }
    }
  }

  .faq-section {
    padding: 100px 0;
    background: #f8f9fa;

    .section-title {
      font-size: 4rem;
      font-weight: 700;
      color: $landing-primary-color;
      margin-bottom: 3rem;
      line-height: 1.2;
    }

    .faq-item {
      background: white;
      border: 2px solid #e5eaee;
      border-radius: 12px;
      margin-bottom: 1.5rem;
      overflow: hidden;
      transition: all 0.3s ease;

      &:hover {
        border-color: $landing-primary-color;
      }

      .faq-question {
        padding: 1.5rem 2rem;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        transition: all 0.3s ease;

        &:hover {
          background: #f8f9fa;
        }

        &.active {
          background: #e8f5e9;
        }

        .faq-question-text {
          font-size: 1.5rem;
          font-weight: 600;
          color: #2d4a4d;
          line-height: 1.5;
        }

        .faq-arrow {
          color: $landing-primary-color;
          transition: transform 0.3s ease;
          font-size: 1.2rem;

          &.rotated {
            transform: rotate(180deg);
          }
        }
      }

      .faq-answer {
        padding: 0 2rem 1.5rem 2rem;

        p {
          font-size: 1.3rem;
          color: #5a6d70;
          line-height: 1.8;
        }
      }
    }
  }

  @media (max-width: 768px) {
    .hero-section {
      padding: 100px 24px;
      min-height: 560px;
      background-size: cover;
      background-position: center center;

      .hero-title-main {
        font-size: 2.8rem;
      }

      .hero-title-sub {
        font-size: 2.4rem;
      }

      .hero-description {
        font-size: 1.2rem;
        text-align: center;
        margin-left: auto;
        margin-right: auto;
      }

      .btn-primary-hero {
        font-size: 1rem;
      }
    }

    .section-title {
      font-size: 2rem !important;
    }

    .section-text {
      font-size: 0.9rem !important;
    }

    .section-description {
      font-size: 0.9rem !important;
    }

    .coverage-section {
      .container {
        padding-left: 0;
        padding-right: 0;
        max-width: 100%;
      }

      .row {
        margin-left: 0;
        margin-right: 0;
      }

      .row > [class*="col-"] {
        padding-left: 0;
        padding-right: 0;
      }

      .coverage-card {
        width: 100%;
        margin: 0;
        box-sizing: border-box;
        border-radius: 0;
        padding-left: 1rem;
        padding-right: 1rem;

        .coverage-title {
          font-size: 1.4rem !important;
          font-weight: 700;
          word-wrap: break-word;
          overflow-wrap: break-word;
          line-height: 1.3;
        }

        .coverage-details p {
          font-size: 1.05rem;
        }

        .btn-coverage {
          font-size: 1rem;
        }
      }
    }

    .result-card .result-title,
    .why-important-section .section-title,
    .providers-section .section-title {
      font-size: 1.5rem !important;
    }

    .section-title {
      font-size: 2rem !important;
    }
  }
}
</style>
